import React from 'react';
import type { ReadonlyWatchable } from '@cotera/client/v0/actions/framework';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { cn } from '@cotera/client/v0/lib/utils';
import { Watchable } from '@cotera/client/v0/actions/framework';
import { Icon } from '@cotera/client/app/components/ui/icon';
import {
  DATA_GRID_ROW_NUMBER_WIDTH,
  getDataGridColumnLayout,
} from './column-layout';
import { DataGridHiddenColumnsChip } from './headers/column-visibility';
import { BaseCell, DataGridRowHeightContext } from './cells/base-cell';
import { dataGridEditorKind, parseEditorValue } from './cells/cell-editor';
import {
  DataGridCellDetail,
  DataGridCellDetailEnabledContext,
  DataGridCellDetailModal,
} from './cells/cell-detail';
import { describeFilterValue, toggleStructuredFilterValue } from './filters';
import { DataGridHeader } from './headers/header';
import {
  DATA_GRID_GUTTER_CHEVRON_CLASS,
  RowNumberHeader,
} from './headers/row-number-header';
import { getAddressForCell, getKeyboardAction } from './keyboard';
import {
  buildDataGridClipboardText,
  parseDataGridClipboardText,
} from './clipboard';
import { isCellSelected } from './selection';
import { DataGridOverlay, useDataGridOverlay } from './overlay';
import {
  getVirtualizedColumns,
  getVirtualizedRangeWithOffsets,
} from './virtualization';
import { createRowOffsetIndex } from './row-offsets';
import { DEFAULT_DATA_GRID_DISPLAY_OPTIONS } from './types';
import { dataGridEditKey } from './types';
import type {
  DataGridCellAddress,
  DataGridCellComponentProps,
  DataGridCellContext,
  DataGridCellEdit,
  DataGridCellValue,
  DataGridColumnDisplayOptions,
  DataGridColumnLayout,
  DataGridColumnLayoutItem,
  DataGridEmptyStateProps,
  DataGridColumnStats,
  DataGridColumnStatsSource,
  DataGridEditAdvance,
  DataGridFooterProps,
  DataGridHeaderComponentProps,
  DataGridHeaderContext,
  DataGridProps,
  DataGridRowComponentProps,
  DataGridRowHeight,
  DataGridRowId,
  DataGridRowNumberContext,
  DataGridSort,
  DataGridSortDirection,
  DataGridTopBarProps,
  DataGridVisibleWindow,
} from './types';
import type { DataGridViewModel } from './view-model';

/** Long enough to read the notice, short enough not to sit over a cell. */
const COPIED_NOTICE_MS = 1600;

/**
 * How many edits back Ctrl/Cmd+Z can walk. Deep enough to cover a run of typing
 * or a pasted block, bounded so a long editing session does not hold every
 * value the grid has ever shown.
 */
const UNDO_DEPTH = 100;

const FALSE_WATCHABLE = Watchable.fromValue(false);
const NULL_TOTAL = Watchable.fromValue<number | null>(null);
const EMPTY_STATS = Watchable.fromValue<
  Record<string, DataGridColumnStats | undefined>
>({});
const EMPTY_COLUMN_STATS = Watchable.fromValue<DataGridColumnStats | undefined>(
  undefined
);

const cellNotice = (verb: string, count: number): string =>
  `${verb} ${count.toLocaleString()} ${count === 1 ? 'cell' : 'cells'}`;

const columnLabelFor = (
  columns: { id: string; header: React.ReactNode }[],
  columnId: string
): string => {
  const column = columns.find((candidate) => candidate.id === columnId);
  return typeof column?.header === 'string' ? column.header : columnId;
};

function DefaultTopBar<TRow>({
  columns,
  totalRows,
  totalLoadedRows,
  isLoading,
  setColumnVisible,
}: DataGridTopBarProps<TRow>) {
  // Counts what is on screen: hidden columns are reachable from the badges.
  const visibleColumnCount = columns.filter(
    (column) => column.visible !== false
  ).length;

  return (
    <div className="flex min-h-9 items-center gap-2 border-b border-border bg-background px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">
      <span className="shrink-0 tabular-nums">
        {totalRows !== null
          ? `${totalRows.toLocaleString()} rows`
          : `${totalLoadedRows.toLocaleString()} rows loaded`}{' '}
        · {visibleColumnCount.toLocaleString()} columns
      </span>
      {/* Sorting and filtering are shown on the columns themselves; the only
          state that has nowhere else to live is a hidden column. */}
      <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
        <DataGridHiddenColumnsChip
          columns={columns}
          setColumnVisible={setColumnVisible}
        />
      </div>
      {isLoading ? (
        <span className="shrink-0 text-[11px] text-muted-foreground/70">
          Loading…
        </span>
      ) : null}
    </div>
  );
}

/**
 * The footer gutter's chip: one small pill stating something about the grid's
 * current state, and acting on it when pressed.
 *
 * Shared because there are now four of these — filter, sort, expand, copy —
 * and they had a long class string each, copied. `tone` is the only thing that
 * ever differed: a chip that reports a rule narrowing the grid is tinted,
 * everything else is quiet chrome.
 */
export function DataGridChip({
  label,
  tone = 'muted',
  children,
  onClick,
}: {
  /** The chip's accessible name, which is the action it performs. */
  label: string;
  tone?: 'muted' | 'primary';
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      className={cn(
        'flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider transition-colors',
        tone === 'primary'
          ? 'border-primary/30 bg-primary/10 text-primary hover:bg-primary/20'
          : 'border-border bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** Shared chip for an active filter, clickable to clear it. */
export function DataGridFilterChip({
  label,
  value,
  onClear,
}: {
  label: string;
  value: unknown;
  onClear: () => void;
}) {
  const described = describeFilterValue(value);
  return (
    <DataGridChip
      label={`Clear ${label} filter`}
      tone="primary"
      onClick={onClear}
    >
      <span className="max-w-48 truncate">
        {label}
        {described === '' ? '' : `: ${described}`}
      </span>
      <Icon icon="x-mark" size="small" className="opacity-70" />
    </DataGridChip>
  );
}

/** One active sort, clickable to remove just that sort. */
export function DataGridSortChip({
  label,
  direction,
  onClear,
}: {
  label: string;
  direction: DataGridSortDirection;
  onClear: () => void;
}) {
  return (
    <DataGridChip label={`Clear ${label} sort`} onClick={onClear}>
      <span className="max-w-48 truncate">{label}</span>
      <span className="font-normal">{direction === 'asc' ? '↑' : '↓'}</span>
      <Icon icon="x-mark" size="small" className="opacity-60" />
    </DataGridChip>
  );
}

/**
 * Chip for the focused cell's expand affordance, sized to sit alongside the
 * sort and filter chips in a footer's gutter. Opens the cell-detail modal.
 */
export function DataGridExpandChip({
  label,
  editable,
  onExpand,
}: {
  label: string;
  editable: boolean;
  onExpand: () => void;
}) {
  return (
    <DataGridChip label={`Expand ${label} cell`} onClick={onExpand}>
      <Icon icon="arrows-pointing-out" size="small" className="opacity-60" />
      <span className="max-w-48 truncate">
        {editable ? 'Expand & edit' : 'Expand'}
      </span>
    </DataGridChip>
  );
}

/**
 * Copies the current selection, and says how much that is. Sized to sit
 * alongside the sort, filter and expand chips.
 */
export function DataGridCopyChip({
  unit,
  count,
  onCopy,
}: {
  unit: 'cells' | 'rows';
  count: number;
  onCopy: () => void;
}) {
  const noun = count === 1 ? unit.slice(0, -1) : unit;
  return (
    <DataGridChip label={`Copy ${count} selected ${noun}`} onClick={onCopy}>
      <Icon icon="clipboard-document" size="small" className="opacity-60" />
      <span className="tabular-nums">
        Copy {count.toLocaleString()} {noun}
      </span>
    </DataGridChip>
  );
}

export function DataGridSortChips({
  columns,
  sorts,
  clearSort,
}: {
  columns: { id: string; header: React.ReactNode }[];
  sorts: DataGridSort[];
  clearSort: (columnId: string) => void;
}) {
  if (sorts.length === 0) {
    return <span className="shrink-0 text-muted-foreground/60">No sort</span>;
  }
  return (
    <>
      {sorts.map((sort) => (
        <DataGridSortChip
          key={`sort-${sort.columnId}`}
          label={columnLabelFor(columns, sort.columnId)}
          direction={sort.direction}
          onClear={() => clearSort(sort.columnId)}
        />
      ))}
    </>
  );
}

function DefaultFooter<TRow>({
  columns,
  sorts,
  filters,
  clearSort,
  clearFilter,
  expandableCell,
  copyableSelection,
}: DataGridFooterProps<TRow>) {
  return (
    <div className="flex min-h-8 items-center gap-3 border-t border-border bg-background px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">
      <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
        {filters.map((filter) => (
          <DataGridFilterChip
            key={`filter-${filter.columnId}`}
            label={columnLabelFor(columns, filter.columnId)}
            value={filter.value}
            onClear={() => clearFilter(filter.columnId)}
          />
        ))}
        <DataGridSortChips
          columns={columns}
          sorts={sorts}
          clearSort={clearSort}
        />
      </div>
      {copyableSelection !== null && copyableSelection !== undefined ? (
        <DataGridCopyChip {...copyableSelection} />
      ) : null}
      {expandableCell !== null && expandableCell !== undefined ? (
        <DataGridExpandChip {...expandableCell} />
      ) : null}
    </div>
  );
}

/**
 * A row's stripe / selection wash. Deliberately translucent and deliberately
 * separate from the opaque base it sits on: the pinned groups repaint the same
 * tint over their own background so a pinned cell matches the row it belongs
 * to without inheriting its transparency.
 */
const rowTintClass = (rowIndex: number, selected: boolean): string =>
  selected ? 'bg-primary/5' : rowIndex % 2 === 0 ? '' : 'bg-muted/20';

function DefaultRow<TRow>({
  children,
  rowIndex,
  selected,
  style,
  onClick,
}: DataGridRowComponentProps<TRow>) {
  const tint = rowTintClass(rowIndex, selected);
  return (
    <div
      role="row"
      aria-rowindex={rowIndex + 2}
      aria-selected={selected}
      className={cn(
        'absolute border-border/60',
        tint === '' ? 'bg-background' : tint
      )}
      style={style}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

const getColumnIdForIndex = <TRow,>(
  columns: DataGridColumnLayoutItem<TRow>[],
  columnIndex: number
): string | null => columns[columnIndex]?.column.id ?? null;

/**
 * Regular columns are absolutely positioned at their layout `x` so the
 * horizontal virtualizer can mount and unmount them freely. Pinned columns sit
 * in the normal flow of their `DataGridPinnedGroup`, which lays them out
 * contiguously and is itself the single sticky element for that side.
 */
/**
 * What the body says when it has no rows to draw.
 *
 * A grid that filters down to nothing used to render its header over blank
 * page, which reads as a broken table rather than as an answer — nothing tells
 * the reader that a filter is why, and the control that would undo it is behind
 * a column menu they have to remember filtering from.
 *
 * So the filtered case names the cause and carries the way out. An unfiltered
 * empty grid gets the plain statement: there is nothing to undo, and offering a
 * button that clears nothing is worse than silence.
 */
export function DataGridEmptyState({
  filterCount,
  clearFilters,
}: DataGridEmptyStateProps) {
  const filtered = filterCount > 0;
  return (
    <div className="flex flex-col items-center gap-1.5 px-6 py-10 text-center">
      <Icon
        icon={filtered ? 'funnel' : 'table-cells'}
        className="text-muted-foreground/40"
      />
      <span className="text-sm font-medium text-foreground">
        {filtered ? 'No rows match these filters' : 'No rows'}
      </span>
      {filtered ? (
        <>
          <span className="max-w-[22rem] text-[11px] leading-snug text-muted-foreground">
            {filterCount === 1
              ? 'One column filter is narrowing this grid to nothing.'
              : `${filterCount} column filters are narrowing this grid to nothing.`}
          </span>
          <button
            type="button"
            className="mt-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
            onClick={clearFilters}
          >
            {filterCount === 1 ? 'Clear filter' : 'Clear filters'}
          </button>
        </>
      ) : null}
    </div>
  );
}

const styleForColumn = <TRow,>({
  item,
  height,
}: {
  item: DataGridColumnLayoutItem<TRow>;
  height: number;
}): React.CSSProperties => {
  if (item.pinned !== null) {
    return {
      position: 'relative',
      width: item.width,
      height,
      flexShrink: 0,
    };
  }

  return {
    position: 'absolute',
    left: item.x,
    top: 0,
    width: item.width,
    height,
  };
};

/**
 * The sticky pane holding one side's pinned columns. Sticking the group rather
 * than each individual cell is what makes pinning opaque: cells and headers are
 * translucent by design (the stripe lives on the row, the header wash is
 * `bg-muted/40`), so a stuck *cell* lets the regular columns scrolling beneath
 * it read straight through. The group supplies one opaque backdrop for the
 * whole band, plus the row's tint on top of it so the band still stripes.
 *
 * No `top` is set — a vertical sticky offset would let the band slide within
 * its row as the row scrolls under the header.
 */
function DataGridPinnedGroup({
  side,
  width,
  height,
  tintClass,
  children,
}: {
  side: 'left' | 'right';
  width: number;
  height: number;
  tintClass?: string;
  children: React.ReactNode;
}) {
  const style: React.CSSProperties = {
    position: 'sticky',
    // Load-bearing rather than cosmetic: pinned columns are in normal flow.
    display: 'flex',
    width,
    height,
    flexShrink: 0,
    zIndex: 20,
    ...(side === 'left'
      ? { left: 0 }
      : // Pushes the group to the end of the flow line, past the absolutely
        // positioned regular columns that contribute no width to it.
        { right: 0, marginLeft: 'auto' }),
  };

  return (
    <div
      className={cn(
        'bg-background',
        // A line, because the columns that scroll pass *underneath* this
        // group. Without one there was nothing to say so: a column scrolled
        // half behind a pinned one showed a blank sliver with a blank header,
        // which reads as an empty column rather than as a column continuing
        // under something. A full-strength border against the `border/70` every
        // other cell carries is enough to mark it; a shadow made the grid look
        // layered, which is more than the boundary needs to say.
        side === 'left' ? 'border-r border-border' : 'border-l border-border'
      )}
      style={style}
    >
      {tintClass !== undefined && tintClass !== '' ? (
        <div
          aria-hidden
          className={cn('pointer-events-none absolute inset-0', tintClass)}
        />
      ) : null}
      {children}
    </div>
  );
}

/**
 * Grid keyboard navigation binds Space, Enter, the arrows and Home/End, and
 * preventDefault()s them. Top bar / footer / cell editors live inside the grid
 * root, so their keystrokes bubble here — typing in one must not be treated as
 * navigation.
 */
const isTextEntryTarget = (target: EventTarget | null): boolean => {
  if (target === null || !(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable) {
    return true;
  }
  const tagName = target.tagName;
  return tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
};

const minColumnIndex = <TRow,>(
  items: DataGridColumnLayoutItem<TRow>[]
): number =>
  items.reduce(
    (min, item) => Math.min(min, item.columnIndex),
    items[0]?.columnIndex ?? 0
  );

const maxColumnIndex = <TRow,>(
  items: DataGridColumnLayoutItem<TRow>[]
): number =>
  items.reduce(
    (max, item) => Math.max(max, item.columnIndex),
    items[0]?.columnIndex ?? 0
  );

type HeaderCellProps<TRow> = {
  item: DataGridColumnLayoutItem<TRow>;
  headerHeight: number;
  viewModel: DataGridViewModel<TRow>;
  sorts: DataGridSort[];
  sortIndex: number | null;
  filterValue: { columnId: string; value: unknown } | null;
  statsFromMap: DataGridColumnStats | null;
  statsSource: DataGridColumnStatsSource | undefined;
  statsExpanded: boolean;
  displayOptions: DataGridColumnDisplayOptions;
  rowHeightPreset: DataGridRowHeight;
  onHeaderStatsVisible: ((columnId: string) => void) | undefined;
  renderActions:
    | ((context: DataGridHeaderContext<TRow>) => React.ReactNode)
    | undefined;
  renderMenuItems:
    | ((context: DataGridHeaderContext<TRow>) => React.ReactNode)
    | undefined;
  HeaderComponent: React.ComponentType<DataGridHeaderComponentProps<TRow>>;
};

/**
 * Wraps one rendered header so it can subscribe to its own stats watchable and
 * announce that it is on screen. Because only pinned + horizontally visible
 * columns are rendered, mounting here is exactly the "stats became visible"
 * signal the host needs.
 */
function HeaderCell<TRow>({
  item,
  headerHeight,
  viewModel,
  sorts,
  sortIndex,
  filterValue,
  statsFromMap,
  statsSource,
  statsExpanded,
  displayOptions,
  rowHeightPreset,
  onHeaderStatsVisible,
  renderActions,
  renderMenuItems,
  HeaderComponent,
}: HeaderCellProps<TRow>) {
  const columnId = item.column.id;
  const statsWatchable = React.useMemo(
    () => statsSource?.get(columnId) ?? EMPTY_COLUMN_STATS,
    [columnId, statsSource]
  );
  const perColumnStats = useWatchableValue(statsWatchable);
  const stats =
    statsSource !== undefined ? perColumnStats ?? null : statsFromMap;

  const requestStatsLoad = React.useCallback(() => {
    if (viewModel.headersExpanded.snapshot()) {
      onHeaderStatsVisible?.(columnId);
    }
  }, [columnId, onHeaderStatsVisible, viewModel.headersExpanded]);

  React.useEffect(() => {
    if (statsExpanded) {
      requestStatsLoad();
    }
  }, [requestStatsLoad, statsExpanded]);

  const context: DataGridHeaderContext<TRow> = {
    column: item.column,
    columnIndex: item.columnIndex,
    sort: sortIndex === null ? null : sorts[sortIndex] ?? null,
    sortIndex,
    sortCount: sorts.length,
    filter: filterValue,
    stats,
    statsExpanded,
    displayOptions,
    rowHeight: rowHeightPreset,
    commands: {
      resize(width) {
        viewModel.resizeColumn(columnId, width);
      },
      reorderBefore(sourceColumnId) {
        viewModel.reorderColumn(sourceColumnId, columnId);
      },
      pin(side) {
        viewModel.pinColumn(columnId, side);
      },
      unpin() {
        viewModel.unpinColumn(columnId);
      },
      setSort(direction) {
        viewModel.setSort(columnId, direction);
      },
      toggleSort(direction) {
        viewModel.toggleSort(columnId, direction);
      },
      setFilter(value) {
        viewModel.setFilter(columnId, value);
      },
      clearFilter() {
        viewModel.clearFilter(columnId);
      },
      toggleStatFilter(value) {
        const next = toggleStructuredFilterValue(filterValue?.value, value);
        if (next === null) {
          viewModel.clearFilter(columnId);
        } else {
          viewModel.setFilter(columnId, next);
        }
      },
      requestStatsLoad,
      setDisplayOptions(options) {
        viewModel.setColumnDisplayOptions(columnId, options);
      },
      setRowHeight(next) {
        viewModel.setRowHeight(next);
      },
      hide() {
        viewModel.setColumnVisible(columnId, false);
      },
    },
  };

  return (
    <HeaderComponent
      context={context}
      actions={renderActions?.(context)}
      menuItems={renderMenuItems?.(context)}
      style={styleForColumn({ item, height: headerHeight })}
    />
  );
}

type RowShellProps<TRow> = {
  row: TRow;
  rowId: DataGridRowId;
  rowIndex: number;
  selected: boolean;
  rowHeight: number;
  /** Absolute top within the scroll content, expansions above already counted. */
  rowTop: number;
  /** Null when the grid has no detail renderer, which hides the chevron. */
  expanded: boolean | null;
  onToggleExpand: (rowId: DataGridRowId) => void;
  contentWidth: number;
  rowNumberWidth: number;
  layout: DataGridColumnLayout<TRow>;
  leftPinnedColumns: DataGridColumnLayoutItem<TRow>[];
  regularColumns: DataGridColumnLayoutItem<TRow>[];
  rightPinnedColumns: DataGridColumnLayoutItem<TRow>[];
  contextForCell: (
    row: TRow,
    rowIndex: number,
    item: DataGridColumnLayoutItem<TRow>
  ) => DataGridCellContext<TRow>;
  onRowClick: (rowId: DataGridRowId, event: React.MouseEvent) => void;
  onCellClick: (
    context: DataGridCellContext<TRow>,
    event: React.MouseEvent
  ) => void;
  headerHeight: number;
  CellComponent: React.ComponentType<DataGridCellComponentProps<TRow>>;
  RowComponent: React.ComponentType<DataGridRowComponentProps<TRow>>;
  renderRowNumber?: (
    context: DataGridRowNumberContext<TRow>
  ) => React.ReactNode;
};

/**
 * Memoized so a granular row patch (which preserves the identity of every
 * untouched row object) only re-renders the row that actually changed.
 */
const RowShellInner = <TRow,>({
  row,
  rowId,
  rowIndex,
  selected,
  rowHeight,
  rowTop,
  expanded,
  onToggleExpand,
  contentWidth,
  rowNumberWidth,
  layout,
  leftPinnedColumns,
  regularColumns,
  rightPinnedColumns,
  contextForCell,
  onRowClick,
  onCellClick,
  headerHeight,
  CellComponent,
  RowComponent,
  renderRowNumber,
}: RowShellProps<TRow>) => {
  const tint = rowTintClass(rowIndex, selected);
  // Read from context rather than taken as a prop: it is the same value for
  // every row, and a prop would invalidate the memo on all of them.
  const gutterAlignsTop = React.useContext(DataGridRowHeightContext) === 'tall';
  const renderCell = (item: DataGridColumnLayoutItem<TRow>) => {
    const context = contextForCell(row, rowIndex, item);
    return (
      <CellComponent
        key={item.column.id}
        context={context}
        style={styleForColumn({ item, height: rowHeight })}
        onClick={(event) => onCellClick(context, event)}
      />
    );
  };

  return (
    <RowComponent
      row={row}
      rowId={rowId}
      rowIndex={rowIndex}
      selected={selected}
      style={{
        // Flex so the sticky pinned groups resolve their resting position from
        // a horizontal flow instead of stacking vertically.
        display: 'flex',
        top: headerHeight + rowTop,
        height: rowHeight,
        width: contentWidth,
      }}
      onClick={(event) => onRowClick(rowId, event)}
    >
      <DataGridPinnedGroup
        side="left"
        width={layout.leftPinnedWidth}
        height={rowHeight}
        tintClass={tint}
      >
        <button
          type="button"
          role="gridcell"
          aria-colindex={1}
          // `relative` so the gutter paints over the group's tint layer, which
          // keeps it reading as chrome rather than as part of the striped body.
          className={cn(
            'group/gutter relative flex justify-end border-b border-r border-border/70 bg-background px-2 text-xs tabular-nums text-muted-foreground',
            // Tall rows top-align their cell text, and a row number floating
            // in the middle of one no longer names the line it belongs to.
            gutterAlignsTop ? 'items-start pt-2' : 'items-center'
          )}
          style={{ width: rowNumberWidth, height: rowHeight, flexShrink: 0 }}
          onClick={(event) => {
            event.stopPropagation();
            onRowClick(rowId, event);
          }}
        >
          {expanded !== null ? (
            // Nested inside the gutter button rather than beside it, so adding
            // expansion does not change the gutter's width and shift every
            // column's `x`.
            <span
              role="button"
              tabIndex={0}
              aria-label={expanded ? 'Collapse row' : 'Expand row'}
              aria-expanded={expanded}
              className={DATA_GRID_GUTTER_CHEVRON_CLASS}
              onClick={(event) => {
                event.stopPropagation();
                onToggleExpand(rowId);
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') {
                  return;
                }
                event.preventDefault();
                event.stopPropagation();
                onToggleExpand(rowId);
              }}
            >
              <Icon
                icon={expanded ? 'chevron-down' : 'chevron-right'}
                size="small"
              />
            </span>
          ) : null}
          {renderRowNumber !== undefined
            ? renderRowNumber({ rowId, rowIndex, row })
            : rowIndex + 1}
        </button>
        {leftPinnedColumns.map(renderCell)}
      </DataGridPinnedGroup>
      {regularColumns.map(renderCell)}
      {rightPinnedColumns.length > 0 ? (
        <DataGridPinnedGroup
          side="right"
          width={layout.rightPinnedWidth}
          height={rowHeight}
          tintClass={tint}
        >
          {rightPinnedColumns.map(renderCell)}
        </DataGridPinnedGroup>
      ) : null}
    </RowComponent>
  );
};

const RowShell = React.memo(RowShellInner) as typeof RowShellInner;

export function DataGrid<TRow>({
  rows: rowsWatchableProp,
  rowSource,
  getRowId,
  viewModel,
  className,
  hasMore: hasMoreProp,
  isLoadingMore: isLoadingMoreProp = false,
  columnStats: columnStatsProp,
  columnStatsSource,
  onLoadMore,
  onVisibleWindowChange,
  onSortChange,
  onFilterChange,
  onHeaderStatsVisible,
  renderColumnHeaderActions,
  renderColumnMenuItems,
  onCellEdit,
  onSaveEdits,
  onRevertEdits,
  autoSaveEdits = false,
  enableCellDetail = false,
  onSelectCell,
  onCellSelectionChange,
  onRowSelectionChange,
  renderEmpty,
  renderRowDetail,
  renderRowNumber,
  showStatsToggle = true,
  CellComponent,
  HeaderComponent,
  RowComponent,
  TopBarComponent,
  FooterComponent,
}: DataGridProps<TRow>) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const topBarRef = React.useRef<HTMLDivElement | null>(null);
  const footerRef = React.useRef<HTMLDivElement | null>(null);
  const loadMoreSentinelRef = React.useRef<HTMLDivElement | null>(null);
  const [containerSize, setContainerSize] = React.useState({
    width: 800,
    height: 520,
  });
  const [chromeHeight, setChromeHeight] = React.useState({
    top: 37,
    bottom: 0,
  });
  const [loadMoreInFlight, setLoadMoreInFlight] = React.useState(false);
  // The cell whose detail modal is open, addressed rather than held by value so
  // it survives a re-query that hands back new row objects for the same rows.
  const [cellDetailAddress, setCellDetailAddress] =
    React.useState<DataGridCellAddress | null>(null);
  const overlay = useDataGridOverlay();
  const fallbackRows: ReadonlyWatchable<TRow[]> = React.useMemo(
    () => Watchable.fromValue<TRow[]>([]),
    []
  );

  const rowsWatchable: ReadonlyWatchable<TRow[]> =
    rowsWatchableProp ?? rowSource?.rows ?? fallbackRows;
  const rows = useWatchableValue(rowsWatchable);
  const columns = useWatchableValue(viewModel.columns);
  const focusedCell = useWatchableValue(viewModel.focusedCell);
  const selectedRowIds = useWatchableValue(viewModel.selectedRowIds);
  const selectedCellRanges = useWatchableValue(viewModel.selectedCellRanges);
  const sorts = useWatchableValue(viewModel.sorts);
  const filters = useWatchableValue(viewModel.filters);
  const rowHeight = useWatchableValue(viewModel.rowHeight);
  const rowHeightPreset = useWatchableValue(viewModel.rowHeightPreset);
  /**
   * What the last clipboard or bulk-edit keystroke did, until it times out.
   *
   * Those keystrokes are otherwise silent — a copy changes nothing on the page
   * at all — and the reader is left to paste somewhere else to find out whether
   * it worked and how much it took.
   */
  const [actionNotice, setActionNotice] = React.useState<string | null>(null);
  const expansionHeightValue = useWatchableValue(viewModel.expansionHeight);
  const expandedRowIds = useWatchableValue(viewModel.expandedRowIds);
  const headerHeight = useWatchableValue(viewModel.headerHeight);
  const headersExpanded = useWatchableValue(viewModel.headersExpanded);
  const editingCell = useWatchableValue(viewModel.editingCell);
  const editSeed = useWatchableValue(viewModel.editSeed);
  const pendingEdits = useWatchableValue(viewModel.pendingEdits);
  const displayOptionsByColumn = useWatchableValue(
    viewModel.columnDisplayOptions
  );
  const scrollX = useWatchableValue(viewModel.scrollX);
  const scrollY = useWatchableValue(viewModel.scrollY);
  const rewindRequests = useWatchableValue(viewModel.rewindRequests);
  const columnStats = useWatchableValue(columnStatsProp ?? EMPTY_STATS);
  const rowSourceHasMoreWatchable = React.useMemo(
    () => rowSource?.hasMore ?? FALSE_WATCHABLE,
    [rowSource]
  );
  const rowSourceLoadingWatchable = React.useMemo(
    () => rowSource?.isLoading ?? FALSE_WATCHABLE,
    [rowSource]
  );
  const rowSourceTotalRowsWatchable = React.useMemo(
    () => rowSource?.totalRows ?? NULL_TOTAL,
    [rowSource]
  );
  const totalRows = useWatchableValue(rowSourceTotalRowsWatchable);
  const rowSourceHasMore = useWatchableValue(rowSourceHasMoreWatchable);
  const rowSourceLoading = useWatchableValue(rowSourceLoadingWatchable);
  const hasMore =
    rowSource !== undefined ? rowSourceHasMore : hasMoreProp === true;
  const isLoadingMore =
    rowSource !== undefined ? rowSourceLoading : isLoadingMoreProp;
  const totalLoadedRows = rows.length;
  // Recomputed on resize so the columns keep filling the width: a grid whose
  // columns underfill stretches them rather than leaving a bare strip beside
  // the last one. Zero while the container is still being measured, which reads
  // as "no width to fill" and leaves the natural widths alone.
  const layout = React.useMemo(
    () =>
      getDataGridColumnLayout(
        columns,
        DATA_GRID_ROW_NUMBER_WIDTH,
        containerSize.width
      ),
    [columns, containerSize.width]
  );
  const ResolvedCellComponent = CellComponent ?? BaseCell<TRow>;
  const ResolvedHeaderComponent = HeaderComponent ?? DataGridHeader<TRow>;
  const ResolvedRowComponent = RowComponent ?? DefaultRow<TRow>;
  const ResolvedTopBarComponent = TopBarComponent ?? DefaultTopBar<TRow>;
  const ResolvedFooterComponent = FooterComponent ?? DefaultFooter<TRow>;
  const bodyHeight = Math.max(
    0,
    containerSize.height - chromeHeight.top - chromeHeight.bottom
  );
  const regularViewportWidth = Math.max(
    0,
    containerSize.width - layout.leftPinnedWidth - layout.rightPinnedWidth
  );
  // Expansion is off unless a detail renderer and a height are both supplied,
  // which keeps the geometry identical to before for every existing grid.
  const expansionHeight =
    renderRowDetail === undefined ? 0 : expansionHeightValue;
  const expandedIndices = React.useMemo(() => {
    if (expansionHeight <= 0 || expandedRowIds.size === 0) {
      return [];
    }
    const indices: number[] = [];
    rows.forEach((row, index) => {
      if (expandedRowIds.has(getRowId(row))) {
        indices.push(index);
      }
    });
    return indices;
  }, [expandedRowIds, expansionHeight, getRowId, rows]);
  const rowOffsets = React.useMemo(
    () =>
      createRowOffsetIndex({
        rowCount: rows.length,
        rowHeight,
        expandedIndices,
        expansionHeight,
      }),
    [expandedIndices, expansionHeight, rowHeight, rows.length]
  );
  const virtualizedRows = React.useMemo(
    () =>
      getVirtualizedRangeWithOffsets({
        itemCount: rows.length,
        offsets: rowOffsets,
        containerSize: bodyHeight - headerHeight,
        scrollOffset: scrollY,
      }),
    [bodyHeight, headerHeight, rowOffsets, rows.length, scrollY]
  );
  const virtualizedRegularColumns = React.useMemo(
    () =>
      getVirtualizedColumns({
        columns: layout.regular,
        containerSize: regularViewportWidth,
        scrollOffset: scrollX,
      }),
    [layout.regular, regularViewportWidth, scrollX]
  );
  const visibleRegularColumns = React.useMemo(
    () =>
      layout.regular.slice(
        virtualizedRegularColumns.startIndex,
        virtualizedRegularColumns.endIndex + 1
      ),
    [
      layout.regular,
      virtualizedRegularColumns.endIndex,
      virtualizedRegularColumns.startIndex,
    ]
  );
  const renderedColumns = React.useMemo(
    () => [
      ...layout.leftPinned,
      ...visibleRegularColumns,
      ...layout.rightPinned,
    ],
    [layout.leftPinned, layout.rightPinned, visibleRegularColumns]
  );

  const displayOptionsFor = React.useCallback(
    (columnId: string): DataGridColumnDisplayOptions =>
      displayOptionsByColumn[columnId] ?? DEFAULT_DATA_GRID_DISPLAY_OPTIONS,
    [displayOptionsByColumn]
  );

  React.useEffect(() => {
    viewModel.setTotalLoadedRows(rows.length);
    if (rowSource !== undefined) {
      viewModel.setTotalRows(totalRows);
    }
  }, [rowSource, rows.length, totalRows, viewModel]);

  React.useEffect(() => {
    const element = containerRef.current;
    if (element === null) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry !== undefined) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const top = topBarRef.current;
    const bottom = footerRef.current;
    const measure = () =>
      setChromeHeight({
        top: top?.offsetHeight ?? 0,
        bottom: bottom?.offsetHeight ?? 0,
      });
    measure();
    const observer = new ResizeObserver(measure);
    if (top !== null) {
      observer.observe(top);
    }
    if (bottom !== null) {
      observer.observe(bottom);
    }
    return () => observer.disconnect();
  }, []);

  const visibleWindow = React.useMemo<DataGridVisibleWindow>(() => {
    const columnsForWindow =
      renderedColumns.length > 0 ? renderedColumns : layout.visibleColumns;
    return {
      startRowIndex: virtualizedRows.startIndex,
      endRowIndex: virtualizedRows.endIndex,
      startColumnIndex: minColumnIndex(columnsForWindow),
      endColumnIndex: maxColumnIndex(columnsForWindow),
    };
  }, [
    layout.visibleColumns,
    renderedColumns,
    virtualizedRows.endIndex,
    virtualizedRows.startIndex,
  ]);

  React.useEffect(() => {
    void rowSource?.loadWindow?.(visibleWindow);
    onVisibleWindowChange?.(visibleWindow);
  }, [onVisibleWindowChange, rowSource, visibleWindow]);

  const loadMoreRows = React.useCallback(() => {
    if (hasMore === false || isLoadingMore || loadMoreInFlight) {
      return;
    }
    const loadMore = rowSource?.loadMore ?? onLoadMore;
    if (loadMore === undefined) {
      return;
    }
    setLoadMoreInFlight(true);
    void Promise.resolve(loadMore()).finally(() => setLoadMoreInFlight(false));
  }, [hasMore, isLoadingMore, loadMoreInFlight, onLoadMore, rowSource]);

  React.useEffect(() => {
    if (rows.length === 0 || virtualizedRows.endIndex >= rows.length - 10) {
      loadMoreRows();
    }
  }, [loadMoreRows, rows.length, virtualizedRows.endIndex]);

  React.useEffect(() => {
    const root = scrollRef.current;
    const target = loadMoreSentinelRef.current;
    if (root === null || target === null || hasMore === false) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting === true) {
          loadMoreRows();
        }
      },
      { root, rootMargin: '240px 0px', threshold: 0 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loadMoreRows, rows.length]);

  React.useEffect(() => {
    onSortChange?.(sorts);
  }, [onSortChange, sorts]);

  React.useEffect(() => {
    onFilterChange?.(filters);
  }, [filters, onFilterChange]);

  React.useEffect(() => {
    onCellSelectionChange?.(selectedCellRanges);
  }, [onCellSelectionChange, selectedCellRanges]);

  React.useEffect(() => {
    onRowSelectionChange?.(selectedRowIds);
  }, [onRowSelectionChange, selectedRowIds]);

  React.useEffect(() => {
    if (actionNotice === null) {
      return;
    }
    const timer = setTimeout(() => setActionNotice(null), COPIED_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [actionNotice]);

  /**
   * The horizontal half of {@link scrollToCell}, on its own.
   *
   * A pinned column is already on screen by definition, so it is left alone —
   * scrolling for one would move the grid under the reader to no effect.
   */
  /**
   * Puts the body back at the top when {@link DataGridViewModel.rewind} asks.
   *
   * Vertical only. Horizontal scroll is where the reader last went looking for
   * a column, and a new set of rows is no reason to take that back.
   *
   * Skipped on the first run, which is not a request — the counter starts at
   * zero and this would otherwise fight a grid mounted mid-page.
   */
  const lastRewind = React.useRef(rewindRequests);
  React.useEffect(() => {
    if (rewindRequests === lastRewind.current) {
      return;
    }
    lastRewind.current = rewindRequests;
    const scrollElement = scrollRef.current;
    if (scrollElement !== null) {
      scrollElement.scrollTop = 0;
      viewModel.scrollY.set(0);
    }
  }, [rewindRequests, viewModel.scrollY]);

  const scrollColumnIntoView = React.useCallback(
    (columnIndex: number) => {
      const scrollElement = scrollRef.current;
      const column = layout.visibleColumns[columnIndex];
      if (scrollElement === null || column === undefined) {
        return;
      }
      if (column.pinned !== null) {
        return;
      }
      const leftEdge = column.x - layout.leftPinnedWidth;
      const rightEdge = leftEdge + column.width;
      if (leftEdge < scrollElement.scrollLeft) {
        scrollElement.scrollLeft = Math.max(0, leftEdge - 16);
      } else if (rightEdge > scrollElement.scrollLeft + regularViewportWidth) {
        scrollElement.scrollLeft = rightEdge - regularViewportWidth + 16;
      }
    },
    [layout.leftPinnedWidth, layout.visibleColumns, regularViewportWidth]
  );

  const scrollToCell = React.useCallback(
    (rowIndex: number, columnIndex: number) => {
      const scrollElement = scrollRef.current;
      const column = layout.visibleColumns[columnIndex];
      if (scrollElement === null || column === undefined) {
        return;
      }

      // Only the row itself needs to come into view, not its detail panel —
      // scrolling a tall panel fully into frame would push the row off the top.
      const rowTop = headerHeight + rowOffsets.offsetForIndex(rowIndex);
      const rowBottom = rowTop + rowHeight;
      if (rowTop < scrollElement.scrollTop + headerHeight) {
        scrollElement.scrollTop = Math.max(0, rowTop - headerHeight);
      } else if (
        rowBottom >
        scrollElement.scrollTop + scrollElement.clientHeight
      ) {
        scrollElement.scrollTop = rowBottom - scrollElement.clientHeight;
      }

      scrollColumnIntoView(columnIndex);
    },
    [
      headerHeight,
      layout.visibleColumns,
      rowHeight,
      rowOffsets,
      scrollColumnIntoView,
    ]
  );

  // Stable so the memoized RowShell is not invalidated for every row on each
  // render, which would defeat the granular-patch optimisation.
  const handleToggleExpand = React.useCallback(
    (rowId: DataGridRowId) => {
      viewModel.toggleRowExpanded(rowId);
    },
    [viewModel]
  );

  const focusCell = React.useCallback(
    (rowIndex: number, columnIndex: number) => {
      const columnId = getColumnIdForIndex(layout.visibleColumns, columnIndex);
      if (columnId === null) {
        return;
      }
      const address = getAddressForCell({
        rows,
        getRowId,
        rowIndex,
        columnId,
        columnIndex,
      });
      if (address !== null) {
        viewModel.focusCell(address);
        scrollToCell(rowIndex, columnIndex);
      }
    },
    [getRowId, layout.visibleColumns, rows, scrollToCell, viewModel]
  );

  /**
   * Keeps the focused cell on screen when focus moves for a reason the user did
   * not scroll to — a row added at the bottom of a long grid, a commit that
   * advanced past the last visible row.
   *
   * Keyed on the address rather than the object: re-running this on every
   * render that happens to produce a new one would drag the viewport back to
   * the focused cell while the user is reading somewhere else.
   */
  const lastFocusKey = React.useRef<string | null>(null);
  React.useEffect(() => {
    const key =
      focusedCell === null
        ? null
        : `${String(focusedCell.rowId)}\u0000${focusedCell.columnId}`;
    if (key === lastFocusKey.current) {
      return;
    }
    lastFocusKey.current = key;
    if (focusedCell !== null) {
      scrollToCell(focusedCell.rowIndex, focusedCell.columnIndex);
    }
  }, [focusedCell, scrollToCell]);

  /**
   * Where an editable cell lives, or null when that address no longer names
   * one. The single question every keyboard edit path has to answer first.
   */
  const editableCellAt = React.useCallback(
    (address: DataGridCellAddress | null) => {
      if (address === null) {
        return null;
      }
      const row = rows[address.rowIndex];
      const item = layout.visibleColumns[address.columnIndex];
      if (row === undefined || item === undefined) {
        return null;
      }
      if (item.column.computed === true || item.column.editable !== true) {
        return null;
      }
      return { row, item };
    },
    [layout.visibleColumns, rows]
  );

  /**
   * Moves focus one cell in a direction, staying inside the grid. What Enter
   * and Tab do after committing, so a column of values is typed rather than
   * navigated.
   */
  const moveFocus = React.useCallback(
    (
      from: DataGridCellAddress,
      advance: Exclude<DataGridEditAdvance, null>
    ) => {
      const rowStep = advance === 'down' ? 1 : advance === 'up' ? -1 : 0;
      const columnStep = advance === 'right' ? 1 : advance === 'left' ? -1 : 0;
      const rowIndex = Math.max(
        0,
        Math.min(rows.length - 1, from.rowIndex + rowStep)
      );
      const columnIndex = Math.max(
        0,
        Math.min(
          layout.visibleColumns.length - 1,
          from.columnIndex + columnStep
        )
      );
      focusCell(rowIndex, columnIndex);
    },
    [focusCell, layout.visibleColumns.length, rows.length]
  );

  /**
   * What Ctrl/Cmd+Z puts back, newest last.
   *
   * Its own stack rather than a read of the pending edits: this grid may be
   * saving as it goes, which clears those on every commit, and even when it is
   * not they only hold the latest value per cell — so a second edit to the same
   * cell would have nothing to step back to. A ref because nothing renders from
   * it; the undo itself is a normal edit, and re-recording one would make undo
   * its own redo.
   */
  const undoStack = React.useRef<
    { address: DataGridCellAddress; previousValue: unknown }[]
  >([]);
  const undoing = React.useRef(false);
  const recordUndo = React.useCallback(
    (entry: {
      address: DataGridCellAddress;
      previousValue: unknown;
      nextValue: DataGridCellValue;
    }) => {
      if (undoing.current || entry.previousValue === entry.nextValue) {
        return;
      }
      undoStack.current = [
        ...undoStack.current.slice(-(UNDO_DEPTH - 1)),
        { address: entry.address, previousValue: entry.previousValue },
      ];
    },
    []
  );

  /**
   * Writes a value into one cell without going through its editor: the shared
   * path for pasting, clearing and undoing. Returns the edit it made, so a bulk
   * caller can save them in one batch rather than one request per cell.
   */
  const editCell = React.useCallback(
    (
      address: DataGridCellAddress,
      nextValue: DataGridCellValue
    ): DataGridCellEdit | null => {
      const target = editableCellAt(address);
      if (target === null) {
        return null;
      }
      const loadedValue = target.item.column.getValue(target.row);
      if (loadedValue === nextValue) {
        return null;
      }
      const edit = viewModel.commitEdit(address, nextValue, loadedValue);
      const reported: DataGridCellEdit = edit ?? {
        rowId: address.rowId,
        columnId: address.columnId,
        previousValue: loadedValue,
        value: nextValue,
      };
      recordUndo({
        address,
        previousValue: loadedValue,
        nextValue,
      });
      onCellEdit?.(reported);
      return reported;
    },
    [editableCellAt, onCellEdit, recordUndo, viewModel]
  );

  /**
   * What happens to committed edits once they exist: on a grid that saves as it
   * goes, one save for the batch and a clear either way, so no cell is left
   * looking dirty. A grid with a Save/Revert bar keeps them pending instead.
   *
   * Shared by the editor's own commit and every bulk write, which otherwise
   * each carried their own copy of this rule.
   */
  const persistEdits = React.useCallback(
    (edits: DataGridCellEdit[]) => {
      if (!autoSaveEdits) {
        return;
      }
      if (edits.length > 0) {
        // The save handler owns reporting its own failure, so swallow the
        // rejection rather than letting it escape unhandled.
        void Promise.resolve(onSaveEdits?.(edits)).catch(() => undefined);
      }
      viewModel.clearEdits();
    },
    [autoSaveEdits, onSaveEdits, viewModel]
  );

  /**
   * Applies a batch of cell writes and settles them together.
   *
   * One `onSaveEdits` for the whole batch, not one per cell: a pasted block is
   * a single act, and a grid that saves as it goes would otherwise turn it into
   * a request per cell.
   */
  const editCells = React.useCallback(
    (writes: { address: DataGridCellAddress; value: DataGridCellValue }[]) => {
      const edits = writes
        .map((write) => editCell(write.address, write.value))
        .filter((edit): edit is DataGridCellEdit => edit !== null);
      if (edits.length === 0) {
        return 0;
      }
      persistEdits(edits);
      return edits.length;
    },
    [editCell, persistEdits]
  );

  /**
   * Every cell the current selection covers, in reading order — what a clear
   * or a paste anchor is worked out from.
   */
  const selectedAddresses = React.useCallback((): DataGridCellAddress[] => {
    const ranges = viewModel.selectedCellRanges.snapshot();
    const focused = viewModel.focusedCell.snapshot();
    const addresses: DataGridCellAddress[] = [];
    if (ranges.length === 0) {
      return focused === null ? [] : [focused];
    }
    for (const [rowIndex, row] of rows.entries()) {
      for (const item of layout.visibleColumns) {
        const address: DataGridCellAddress = {
          rowId: getRowId(row),
          rowIndex,
          columnId: item.column.id,
          columnIndex: item.columnIndex,
        };
        if (isCellSelected(address, ranges)) {
          addresses.push(address);
        }
      }
    }
    return addresses;
  }, [
    getRowId,
    layout.visibleColumns,
    rows,
    viewModel.focusedCell,
    viewModel.selectedCellRanges,
  ]);

  const contextForCell = React.useCallback(
    (row: TRow, rowIndex: number, item: DataGridColumnLayoutItem<TRow>) => {
      const rowId = getRowId(row);
      const value = item.column.getValue(row);
      const address = {
        rowId,
        rowIndex,
        columnId: item.column.id,
        columnIndex: item.columnIndex,
      };
      const anchorRef: React.MutableRefObject<HTMLElement | null> = {
        current: null,
      };
      const cellSelected = isCellSelected(address, selectedCellRanges);
      const rowSelected = selectedRowIds.has(rowId);
      const editable =
        item.column.computed !== true && item.column.editable === true;
      const context: DataGridCellContext<TRow> = {
        row,
        value,
        column: item.column,
        address,
        displayOptions: displayOptionsFor(item.column.id),
        state: {
          focused:
            focusedCell?.rowId === rowId &&
            focusedCell.columnId === item.column.id,
          selected: rowSelected || cellSelected,
          rowSelected,
          cellSelected,
          hovered: false,
          visible:
            rowIndex >= virtualizedRows.startIndex &&
            rowIndex <= virtualizedRows.endIndex,
          editable,
          editing:
            editingCell?.rowId === rowId &&
            editingCell.columnId === item.column.id,
          editSeed:
            editingCell?.rowId === rowId &&
            editingCell.columnId === item.column.id
              ? editSeed
              : null,
          dirty:
            pendingEdits[dataGridEditKey(rowId, item.column.id)] !== undefined,
        },
        anchorRef,
        commands: {
          focus() {
            viewModel.focusCell(address);
          },
          select() {
            viewModel.selectCell(address);
          },
          toggleSelectOnly() {
            viewModel.toggleSelectCellOnly(address);
          },
          toggleSelect() {
            viewModel.toggleCell(address);
          },
          selectRange() {
            const anchor = viewModel.cellSelectionAnchor.snapshot() ?? address;
            viewModel.selectCellRange(anchor, address);
          },
          openOverlay(content) {
            if (anchorRef.current !== null) {
              overlay.openOverlay(anchorRef.current, content);
            }
          },
          closeOverlay() {
            overlay.closeOverlay();
          },
          openDetail() {
            const detail = item.column.renderDetail?.(context);
            if (detail !== undefined && anchorRef.current !== null) {
              overlay.openOverlay(anchorRef.current, detail);
            }
          },
          expandCell() {
            setCellDetailAddress(address);
          },
          beginEdit(seed) {
            if (editable) {
              viewModel.beginEdit(address, seed);
            }
          },
          commitEdit(nextValue, advance) {
            const edit = viewModel.commitEdit(address, nextValue, value);
            recordUndo({ address, previousValue: value, nextValue });
            // Reported even when the value went back to the original and there
            // is no edit to save: the host applied the intermediate value to
            // its own rows and has to be told to put that back. This is what
            // separates an editor commit from a bulk write, which skips a cell
            // that already holds the value being written.
            onCellEdit?.(
              edit ?? {
                rowId,
                columnId: item.column.id,
                previousValue: value,
                value: nextValue,
              }
            );
            persistEdits(edit === null ? [] : [edit]);
            if (advance !== undefined && advance !== null) {
              moveFocus(address, advance);
            }
          },
          cancelEdit() {
            viewModel.cancelEdit();
          },
        },
      };
      return context;
    },
    [
      displayOptionsFor,
      editSeed,
      editingCell,
      focusedCell,
      getRowId,
      moveFocus,
      onCellEdit,
      overlay,
      pendingEdits,
      persistEdits,
      recordUndo,
      selectedCellRanges,
      selectedRowIds,
      viewModel,
      virtualizedRows.endIndex,
      virtualizedRows.startIndex,
    ]
  );

  const selectCell = React.useCallback(() => {
    const currentFocusedCell = viewModel.focusedCell.snapshot();
    if (currentFocusedCell === null) {
      focusCell(0, 0);
      return;
    }
    const row = rows[currentFocusedCell.rowIndex];
    const column = layout.visibleColumns[currentFocusedCell.columnIndex];
    if (row === undefined || column === undefined) {
      return;
    }
    onSelectCell?.(contextForCell(row, currentFocusedCell.rowIndex, column));
  }, [
    contextForCell,
    focusCell,
    layout.visibleColumns,
    onSelectCell,
    rows,
    viewModel.focusedCell,
  ]);

  /**
   * Puts the current selection on the clipboard as TSV, and says so.
   *
   * Returns whether there was anything to copy: with nothing selected the
   * keystroke is left to the browser rather than spent clearing the clipboard.
   */
  const copySelection = React.useCallback((): boolean => {
    const payload = buildDataGridClipboardText({
      rows,
      getRowId,
      columns: layout.visibleColumns.map((item) => item.column),
      ranges: viewModel.selectedCellRanges.snapshot(),
      selectedRowIds: viewModel.selectedRowIds.snapshot(),
      focusedCell: viewModel.focusedCell.snapshot(),
      displayOptionsFor,
    });
    if (payload === null) {
      return false;
    }
    // Optional: a browser without the async clipboard (or a page served over
    // plain http) has none, and the grid should not throw on a keystroke.
    void navigator.clipboard
      ?.writeText(payload.text)
      .then(() => setActionNotice(cellNotice('Copied', payload.cellCount)))
      .catch(() => undefined);
    return true;
  }, [
    displayOptionsFor,
    getRowId,
    layout.visibleColumns,
    rows,
    viewModel.focusedCell,
    viewModel.selectedCellRanges,
    viewModel.selectedRowIds,
  ]);

  /**
   * Puts the last edit back, and says so. Returns whether there was one.
   *
   * The restore is itself a write — it goes to the same place every other edit
   * does — so a grid that saves as it goes persists the old value rather than
   * leaving the screen and the dataset disagreeing.
   */
  const undoLastEdit = React.useCallback((): boolean => {
    const entry = undoStack.current[undoStack.current.length - 1];
    if (entry === undefined) {
      return false;
    }
    undoStack.current = undoStack.current.slice(0, -1);
    undoing.current = true;
    try {
      const restored = editCells([
        {
          address: entry.address,
          value: entry.previousValue as DataGridCellValue,
        },
      ]);
      if (restored === 0) {
        return false;
      }
    } finally {
      undoing.current = false;
    }
    viewModel.focusCell(entry.address);
    setActionNotice('Edit reverted');
    return true;
  }, [editCells, viewModel]);

  /** Empties every editable cell in the selection. */
  const clearSelectedCells = React.useCallback((): boolean => {
    const addresses = selectedAddresses();
    if (addresses.length === 0) {
      return false;
    }
    const cleared = editCells(
      addresses.map((address) => ({ address, value: null }))
    );
    if (cleared > 0) {
      setActionNotice(cellNotice('Cleared', cleared));
    }
    // Claims the key whenever there was a selection to act on, even if every
    // cell in it was already empty or read-only: the alternative is Backspace
    // falling through to the browser and navigating the page back.
    return true;
  }, [editCells, selectedAddresses]);

  /**
   * Writes a pasted block into the grid from the focused cell out.
   *
   * Clamped to the rows and columns that already exist rather than growing the
   * grid: adding rows is the dataset's decision, not the clipboard's, and a
   * paste that silently appended two hundred rows would be far harder to undo
   * than one that stopped at the edge.
   */
  const pasteAt = React.useCallback(
    (text: string): boolean => {
      const block = parseDataGridClipboardText(text);
      if (block.length === 0) {
        return false;
      }
      // The top-left of the selection, or the focused cell when there is none.
      // Built from the smallest row and the smallest column across every
      // corner rather than picked from among them: a range dragged up and to
      // the right has no corner that is both, and taking one anyway would
      // anchor the paste a row or a column away from where it looks like it
      // should land.
      const ranges = viewModel.selectedCellRanges.snapshot();
      const focused = viewModel.focusedCell.snapshot();
      const corners = ranges.flatMap((range) => [range.start, range.end]);
      const anchor =
        corners.length > 0
          ? {
              rowIndex: Math.min(...corners.map((cell) => cell.rowIndex)),
              columnIndex: Math.min(...corners.map((cell) => cell.columnIndex)),
            }
          : focused === null
          ? null
          : { rowIndex: focused.rowIndex, columnIndex: focused.columnIndex };
      if (anchor === null) {
        return false;
      }
      const anchorCell = anchor;

      const writes: {
        address: DataGridCellAddress;
        value: DataGridCellValue;
      }[] = [];
      for (const [rowOffset, line] of block.entries()) {
        const rowIndex = anchorCell.rowIndex + rowOffset;
        const row = rows[rowIndex];
        if (row === undefined) {
          break;
        }
        for (const [columnOffset, raw] of line.entries()) {
          const columnIndex = anchorCell.columnIndex + columnOffset;
          const item = layout.visibleColumns[columnIndex];
          if (item === undefined) {
            break;
          }
          const address: DataGridCellAddress = {
            rowId: getRowId(row),
            rowIndex,
            columnId: item.column.id,
            columnIndex,
          };
          // Through the editor's own parser, so a pasted "42" lands in a number
          // column as a number and not as the string the clipboard gave us.
          const kind =
            item.column.editorKind ??
            dataGridEditorKind(
              item.column.type,
              item.column.getEditOptions?.()
            );
          writes.push({ address, value: parseEditorValue(raw, kind) });
        }
      }
      if (writes.length === 0) {
        return false;
      }
      const pasted = editCells(writes);
      setActionNotice(
        pasted === 0 ? 'Nothing to paste here' : cellNotice('Pasted', pasted)
      );
      return true;
    },
    [
      editCells,
      getRowId,
      layout.visibleColumns,
      rows,
      viewModel.focusedCell,
      viewModel.selectedCellRanges,
    ]
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isTextEntryTarget(event.target)) {
      return;
    }
    const modified = event.metaKey || event.ctrlKey;
    if (modified && event.key.toLowerCase() === 'c') {
      // A text selection the user made by dragging inside a cell is theirs to
      // copy; only an empty one means the keystroke was aimed at the grid.
      const textSelection = window.getSelection();
      if (textSelection !== null && !textSelection.isCollapsed) {
        return;
      }
      if (copySelection()) {
        event.preventDefault();
      }
      return;
    }
    if (modified && !event.shiftKey && event.key.toLowerCase() === 'z') {
      if (undoLastEdit()) {
        event.preventDefault();
      }
      return;
    }
    if (
      !modified &&
      (event.key === 'Delete' || event.key === 'Backspace') &&
      clearSelectedCells()
    ) {
      event.preventDefault();
      return;
    }
    // Typing over a cell edits it, seeded with what was typed — the way a
    // spreadsheet behaves, and the difference between a grid you can fill in
    // and one that has to be double-clicked cell by cell. Anything with a
    // modifier is a shortcut, and anything longer than one character is a named
    // key the navigation below answers.
    if (!modified && !event.altKey && event.key.length === 1) {
      const focusedForTyping = viewModel.focusedCell.snapshot();
      if (editableCellAt(focusedForTyping) !== null) {
        event.preventDefault();
        viewModel.beginEdit(focusedForTyping as DataGridCellAddress, event.key);
        return;
      }
    }
    const currentFocusedCell = viewModel.focusedCell.snapshot();
    const action = getKeyboardAction({
      key: event.key,
      focusedCell: currentFocusedCell,
      rowCount: rows.length,
      columnCount: layout.visibleColumns.length,
      // How many rows are actually on screen right now, which is what a page
      // should move. Expansions make that fewer than the viewport would
      // suggest; with none open this is the old uniform division.
      pageSize: Math.max(
        1,
        rowOffsets.indexForOffset(scrollY + bodyHeight - headerHeight) -
          rowOffsets.indexForOffset(scrollY)
      ),
    });
    if (action.type === 'none') {
      return;
    }
    if (action.type === 'clear-selection') {
      // Claims the key only when there is a selection to drop, so an Escape
      // meant for something layered over the grid still reaches it.
      if (viewModel.selectedRowIds.snapshot().size === 0) {
        return;
      }
      event.preventDefault();
      viewModel.clearSelection();
      return;
    }
    event.preventDefault();

    if (action.type === 'focus') {
      const columnId = getColumnIdForIndex(
        layout.visibleColumns,
        action.columnIndex
      );
      const address =
        columnId === null
          ? null
          : getAddressForCell({
              rows,
              getRowId,
              rowIndex: action.rowIndex,
              columnId,
              columnIndex: action.columnIndex,
            });
      if (address !== null) {
        if (event.shiftKey) {
          const anchor =
            viewModel.cellSelectionAnchor.snapshot() ??
            currentFocusedCell ??
            address;
          viewModel.selectCellRange(anchor, address);
        } else {
          viewModel.focusCell(address);
        }
        scrollToCell(action.rowIndex, action.columnIndex);
      }
      return;
    }
    if (action.type === 'select-focused-row') {
      if (currentFocusedCell !== null) {
        viewModel.toggleRowSelection(currentFocusedCell.rowId);
      }
      return;
    }
    // Enter on an editable cell opens it. Without this the keyboard could move
    // around the grid but never change anything in it — every edit began with a
    // double-click — and `onSelectCell` is a host hook most grids never pass.
    if (editableCellAt(currentFocusedCell) !== null) {
      viewModel.beginEdit(currentFocusedCell as DataGridCellAddress);
      return;
    }
    selectCell();
  };

  const handleCellClick = React.useCallback(
    (context: DataGridCellContext<TRow>, event: React.MouseEvent) => {
      event.stopPropagation();
      if (event.shiftKey) {
        context.commands.selectRange();
      } else if (event.metaKey || event.ctrlKey) {
        context.commands.toggleSelect();
      } else {
        context.commands.toggleSelectOnly();
      }
    },
    []
  );

  const handleRowClick = React.useCallback(
    (rowId: DataGridRowId, event: React.MouseEvent) => {
      if (event.shiftKey && viewModel.selectionAnchor.snapshot() !== null) {
        const selectionAnchor = viewModel.selectionAnchor.snapshot();
        if (selectionAnchor !== null) {
          viewModel.selectRowRange(
            selectionAnchor,
            rowId,
            rowsWatchable.snapshot(),
            getRowId
          );
        }
      } else if (event.metaKey || event.ctrlKey) {
        viewModel.toggleRowSelection(rowId);
      } else {
        viewModel.toggleSelectOnly(rowId);
      }
    },
    [getRowId, rowsWatchable, viewModel]
  );

  const setStatsExpanded = React.useCallback(
    (expanded: boolean) => viewModel.headersExpanded.set(expanded),
    [viewModel.headersExpanded]
  );
  const setRowHeight = React.useCallback(
    (next: DataGridRowHeight) => viewModel.setRowHeight(next),
    [viewModel]
  );
  const clearSort = React.useCallback(
    (columnId: string) => viewModel.setSort(columnId, null),
    [viewModel]
  );
  const clearFilter = React.useCallback(
    (columnId: string) => viewModel.clearFilter(columnId),
    [viewModel]
  );
  const clearFilters = React.useCallback(
    () => viewModel.clearFilters(),
    [viewModel]
  );
  const setColumnVisible = React.useCallback(
    (columnId: string, visible: boolean) =>
      viewModel.setColumnVisible(columnId, visible),
    [viewModel]
  );
  const hiddenColumns = React.useMemo(
    () => columns.filter((column) => column.visible === false),
    [columns]
  );

  const pendingEditList = React.useMemo(
    () =>
      Object.values(pendingEdits).filter(
        (edit): edit is DataGridCellEdit => edit !== undefined
      ),
    [pendingEdits]
  );
  const [editsSaving, setEditsSaving] = React.useState(false);

  const settleEdits = React.useCallback(
    (
        handler:
          | ((edits: DataGridCellEdit[]) => void | Promise<void>)
          | undefined
      ) =>
      () => {
        const edits = pendingEditList;
        if (edits.length === 0 || handler === undefined) {
          viewModel.clearEdits();
          return;
        }
        setEditsSaving(true);
        // Pending state survives a failure so the user can retry. The handler
        // owns reporting the failure, so swallow it here rather than letting it
        // escape as an unhandled rejection.
        void Promise.resolve(handler(edits))
          .then(() => viewModel.clearEdits())
          .catch(() => undefined)
          .finally(() => setEditsSaving(false));
      },
    [pendingEditList, viewModel]
  );

  const contentWidth = Math.max(layout.totalWidth, containerSize.width);
  const contentHeight = Math.max(
    bodyHeight,
    headerHeight + virtualizedRows.totalHeight + (hasMore ? rowHeight : 0)
  );
  // Refreshing (sort/filter/query) rather than paginating: keep the current
  // rows and headers mounted and show progress instead of a skeleton.
  const isRefreshingRows = isLoadingMore && rows.length > 0;

  /**
   * Resolves an addressed cell back to a live context, preferring the stored
   * index but falling back to a row-id lookup so an edit or re-query that
   * reordered the rows still finds the right one. Null once the row or column
   * has gone, which the effect below treats as "close".
   */
  const resolveCellContext = React.useCallback(
    (target: DataGridCellAddress): DataGridCellContext<TRow> | null => {
      let rowIndex = target.rowIndex;
      let row = rows[rowIndex];
      if (row === undefined || getRowId(row) !== target.rowId) {
        rowIndex = rows.findIndex(
          (candidate) => getRowId(candidate) === target.rowId
        );
        row = rowIndex === -1 ? undefined : rows[rowIndex];
      }
      const item = layout.visibleColumns.find(
        (candidate) => candidate.column.id === target.columnId
      );
      if (row === undefined || item === undefined) {
        return null;
      }
      return contextForCell(row, rowIndex, item);
    },
    [contextForCell, getRowId, layout.visibleColumns, rows]
  );

  const cellDetailContext = React.useMemo(
    () =>
      !enableCellDetail || cellDetailAddress === null
        ? null
        : resolveCellContext(cellDetailAddress),
    [cellDetailAddress, enableCellDetail, resolveCellContext]
  );

  // The row or column the open modal pointed at is gone (deleted, filtered out,
  // hidden) — close rather than leave a modal describing nothing.
  React.useEffect(() => {
    if (cellDetailAddress !== null && cellDetailContext === null) {
      setCellDetailAddress(null);
    }
  }, [cellDetailAddress, cellDetailContext]);

  const closeCellDetail = React.useCallback(
    () => setCellDetailAddress(null),
    []
  );

  /**
   * The focused cell's expand affordance, handed to the footer so it can show a
   * chip in the same gutter as the sort/filter chips. Null unless the feature is
   * on, a cell is focused, and that cell still resolves to a live column.
   */
  const expandableCell = React.useMemo(() => {
    if (!enableCellDetail || focusedCell === null) {
      return null;
    }
    const row = rows[focusedCell.rowIndex];
    if (row === undefined || getRowId(row) !== focusedCell.rowId) {
      return null;
    }
    const item = layout.visibleColumns.find(
      (candidate) => candidate.column.id === focusedCell.columnId
    );
    if (item === undefined) {
      return null;
    }
    const label =
      typeof item.column.header === 'string'
        ? item.column.header
        : item.column.id;
    const editable =
      item.column.computed !== true && item.column.editable === true;
    return {
      label,
      editable,
      onExpand: () => setCellDetailAddress(focusedCell),
    };
  }, [enableCellDetail, focusedCell, getRowId, layout.visibleColumns, rows]);

  /**
   * The copy affordance, for whichever selection the clipboard would read.
   *
   * Offered only for a selection the user made — a lone focused cell is a
   * cursor, not a choice, and a chip that appeared the moment anyone clicked a
   * cell would never be off. Ctrl/Cmd+C still copies that cell.
   */
  const copyableSelection = React.useMemo(() => {
    if (selectedCellRanges.length > 0) {
      const count = selectedCellRanges.reduce((total, range) => {
        const rowSpan = Math.abs(range.end.rowIndex - range.start.rowIndex) + 1;
        const columnSpan =
          Math.abs(range.end.columnIndex - range.start.columnIndex) + 1;
        return total + rowSpan * columnSpan;
      }, 0);
      return { unit: 'cells' as const, count, onCopy: () => copySelection() };
    }
    if (selectedRowIds.size > 0) {
      return {
        unit: 'rows' as const,
        count: selectedRowIds.size,
        onCopy: () => copySelection(),
      };
    }
    return null;
  }, [copySelection, selectedCellRanges, selectedRowIds]);

  const renderHeaderCell = (item: DataGridColumnLayoutItem<TRow>) => {
    const sortIndex = sorts.findIndex(
      (candidate) => candidate.columnId === item.column.id
    );
    return (
      <HeaderCell
        key={item.column.id}
        item={item}
        headerHeight={headerHeight}
        viewModel={viewModel}
        sorts={sorts}
        sortIndex={sortIndex === -1 ? null : sortIndex}
        filterValue={
          filters.find((candidate) => candidate.columnId === item.column.id) ??
          null
        }
        statsFromMap={columnStats[item.column.id] ?? null}
        statsSource={columnStatsSource}
        statsExpanded={headersExpanded}
        displayOptions={displayOptionsFor(item.column.id)}
        rowHeightPreset={rowHeightPreset}
        onHeaderStatsVisible={onHeaderStatsVisible}
        renderActions={renderColumnHeaderActions}
        renderMenuItems={renderColumnMenuItems}
        HeaderComponent={ResolvedHeaderComponent}
      />
    );
  };

  const grid = (
    <div
      ref={containerRef}
      role="grid"
      tabIndex={0}
      aria-rowcount={totalRows ?? rows.length}
      aria-colcount={layout.ariaColumnCount}
      aria-activedescendant={
        focusedCell !== null
          ? `dg-cell-${focusedCell.rowIndex}-${focusedCell.columnIndex}`
          : undefined
      }
      className={cn(
        // Sizes entirely from its container: `h-full` fills a block parent,
        // `min-h-0` stops flex's `min-height: auto` from holding the grid open
        // at its content height. Everything below is measured off the
        // ResizeObserver, so there is no size baked in anywhere.
        //
        // No border of its own: the grid fills whatever hosts it, and that host
        // draws the edge — an edge here would only double the host's.
        'relative flex h-full min-h-0 w-full flex-col overflow-hidden rounded-md bg-background outline-none focus:ring-2 focus:ring-primary/40',
        className
      )}
      onKeyDown={handleKeyDown}
      // The native event rather than reading the clipboard ourselves: it hands
      // over the text synchronously and needs no permission prompt.
      onPaste={(event) => {
        if (isTextEntryTarget(event.target)) {
          return;
        }
        const text = event.clipboardData.getData('text/plain');
        if (text !== '' && pasteAt(text)) {
          event.preventDefault();
        }
      }}
    >
      <div ref={topBarRef} className="shrink-0">
        <ResolvedTopBarComponent
          rows={rows}
          columns={columns}
          selectedRowIds={selectedRowIds}
          selectedCellRanges={selectedCellRanges}
          totalRows={totalRows}
          totalLoadedRows={totalLoadedRows}
          sorts={sorts}
          filters={filters}
          isLoading={isLoadingMore}
          statsExpanded={headersExpanded}
          setStatsExpanded={setStatsExpanded}
          rowHeight={rowHeightPreset}
          setRowHeight={setRowHeight}
          clearSort={clearSort}
          clearFilter={clearFilter}
          hiddenColumns={hiddenColumns}
          setColumnVisible={setColumnVisible}
        />
      </div>
      <div
        ref={scrollRef}
        className="relative flex-1 overflow-auto"
        onScroll={(event) => {
          viewModel.scrollX.set(event.currentTarget.scrollLeft);
          viewModel.scrollY.set(event.currentTarget.scrollTop);
        }}
      >
        {isRefreshingRows ? (
          <div
            role="status"
            aria-label="Refreshing rows"
            className="pointer-events-none sticky left-0 top-0 z-40 h-0.5 w-full overflow-hidden bg-primary/10"
          >
            <div className="h-full w-1/3 animate-pulse rounded-full bg-primary/60" />
          </div>
        ) : null}
        <div
          style={{
            width: contentWidth,
            height: contentHeight,
            position: 'relative',
          }}
        >
          <div
            className="sticky top-0 z-30 bg-background"
            style={{
              // Load-bearing rather than cosmetic: see `styleForColumn`.
              display: 'flex',
              width: contentWidth,
              height: headerHeight,
            }}
          >
            <DataGridPinnedGroup
              side="left"
              width={layout.leftPinnedWidth}
              height={headerHeight}
            >
              <RowNumberHeader
                column={layout.rowNumber}
                headerHeight={headerHeight}
                statsExpanded={headersExpanded}
                onToggleStats={() => setStatsExpanded(!headersExpanded)}
                showStatsToggle={showStatsToggle}
              />
              {layout.leftPinned.map(renderHeaderCell)}
            </DataGridPinnedGroup>
            {visibleRegularColumns.map(renderHeaderCell)}
            {layout.rightPinned.length > 0 ? (
              <DataGridPinnedGroup
                side="right"
                width={layout.rightPinnedWidth}
                height={headerHeight}
              >
                {layout.rightPinned.map(renderHeaderCell)}
              </DataGridPinnedGroup>
            ) : null}
          </div>
          {rows
            .slice(virtualizedRows.startIndex, virtualizedRows.endIndex + 1)
            .map((row, offset) => {
              const rowIndex = virtualizedRows.startIndex + offset;
              const rowId = getRowId(row);
              const rowTop = rowOffsets.offsetForIndex(rowIndex);
              const expanded = rowOffsets.isExpanded(rowIndex);
              return (
                <React.Fragment key={rowId}>
                  <RowShell
                    row={row}
                    rowId={rowId}
                    rowIndex={rowIndex}
                    selected={selectedRowIds.has(rowId)}
                    rowHeight={rowHeight}
                    rowTop={rowTop}
                    expanded={renderRowDetail === undefined ? null : expanded}
                    onToggleExpand={handleToggleExpand}
                    headerHeight={headerHeight}
                    contentWidth={contentWidth}
                    rowNumberWidth={layout.rowNumber.width}
                    layout={layout}
                    leftPinnedColumns={layout.leftPinned}
                    regularColumns={visibleRegularColumns}
                    rightPinnedColumns={layout.rightPinned}
                    contextForCell={contextForCell}
                    onRowClick={handleRowClick}
                    onCellClick={handleCellClick}
                    CellComponent={ResolvedCellComponent}
                    RowComponent={ResolvedRowComponent}
                    renderRowNumber={renderRowNumber}
                  />
                  {expanded && renderRowDetail !== undefined ? (
                    <div
                      // Deliberately not `overflow-hidden`: an ancestor with a
                      // clipping overflow becomes the sticky child's
                      // scrollport, so the panel would stick to a box that is
                      // itself scrolling and slide away with the columns.
                      // Clipping belongs on the sticky element instead.
                      className="absolute left-0 border-b border-border/70 bg-muted/30"
                      style={{
                        top: headerHeight + rowTop + rowHeight,
                        height: expansionHeight,
                        width: contentWidth,
                      }}
                    >
                      {/* Sticky so the panel stays put under horizontal
                          scroll — its content is row-scoped, not
                          column-aligned, so scrolling it sideways with the
                          cells would only ever hide it. */}
                      <div
                        className="sticky left-0 h-full overflow-hidden"
                        style={{ width: containerSize.width }}
                      >
                        {renderRowDetail({
                          row,
                          rowId,
                          rowIndex,
                          collapse: () => viewModel.toggleRowExpanded(rowId),
                        })}
                      </div>
                    </div>
                  ) : null}
                </React.Fragment>
              );
            })}
          {/* Sticky-left and sized to the viewport rather than to `contentWidth`,
              so a grid wide enough to scroll does not hide its own explanation
              off to the right. Held back while the first page is still in
              flight: "no rows match" is a wrong answer to a question nobody has
              finished asking. */}
          {rows.length === 0 && !isLoadingMore && !loadMoreInFlight ? (
            <div
              className="sticky left-0 flex items-start justify-center"
              style={{
                position: 'sticky',
                top: headerHeight,
                width: containerSize.width,
              }}
            >
              {renderEmpty === undefined ? (
                <DataGridEmptyState
                  filterCount={filters.length}
                  clearFilters={clearFilters}
                />
              ) : (
                renderEmpty({ filterCount: filters.length, clearFilters })
              )}
            </div>
          ) : null}
          {hasMore ? (
            <div
              ref={loadMoreSentinelRef}
              className="absolute left-0 flex items-center justify-center text-sm text-muted-foreground"
              style={{
                top: headerHeight + rowOffsets.totalHeight,
                height: rowHeight,
                width: contentWidth,
              }}
            >
              {isLoadingMore || loadMoreInFlight
                ? 'Loading more rows…'
                : 'Scroll for more rows'}
            </div>
          ) : null}
        </div>
      </div>
      {/* Anchored to the grid rather than to the scrolling body: inside that
          container it would be placed against the scrolled content and drift
          off with it. Over the body's corner rather than in the chrome because
          a copy answers the selection, and the footer is already carrying the
          sorts, the filters and the query box. */}
      {actionNotice !== null ? (
        <div
          role="status"
          // Above the footer by its measured height, so the pill never lands on
          // the query box or the unsaved-edits bar.
          style={{ bottom: chromeHeight.bottom + 12 }}
          className="pointer-events-none absolute right-3 z-30 rounded-full border border-border bg-background/95 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground shadow-sm"
        >
          {actionNotice}
        </div>
      ) : null}
      <div ref={footerRef} className="shrink-0">
        {!autoSaveEdits && pendingEditList.length > 0 ? (
          <div
            role="status"
            className="flex items-center justify-between gap-3 border-t border-border bg-primary/[0.06] px-3 py-2"
          >
            <div className="flex min-w-0 items-center gap-2">
              {/* Echoes the corner notch drawn on each edited cell. */}
              <span
                aria-hidden
                className="h-0 w-0 shrink-0 border-r-[7px] border-t-[7px] border-r-transparent border-t-primary"
              />
              <span className="truncate text-[11px] font-medium uppercase tracking-wider text-primary">
                {pendingEditList.length.toLocaleString()} unsaved edit
                {pendingEditList.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                disabled={editsSaving}
                className="rounded-md border border-border bg-background px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-primary transition-colors hover:bg-muted disabled:opacity-50"
                onClick={settleEdits(onRevertEdits)}
              >
                Revert
              </button>
              <button
                type="button"
                disabled={editsSaving}
                className="rounded-md bg-primary px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                onClick={settleEdits(onSaveEdits)}
              >
                {editsSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        ) : null}
        <ResolvedFooterComponent
          rows={rows}
          columns={columns}
          totalRows={totalRows}
          totalLoadedRows={totalLoadedRows}
          sorts={sorts}
          filters={filters}
          selectedRowIds={selectedRowIds}
          selectedCellRanges={selectedCellRanges}
          isLoading={isLoadingMore}
          clearSort={clearSort}
          clearFilter={clearFilter}
          clearFilters={clearFilters}
          expandableCell={expandableCell}
          copyableSelection={copyableSelection}
        />
      </div>
      <DataGridOverlay
        overlay={overlay.overlay}
        onClose={overlay.closeOverlay}
      />
      {cellDetailContext !== null ? (
        <DataGridCellDetailModal onClose={closeCellDetail}>
          <DataGridCellDetail
            context={cellDetailContext}
            onClose={closeCellDetail}
          />
        </DataGridCellDetailModal>
      ) : null}
    </div>
  );

  return (
    <DataGridCellDetailEnabledContext.Provider value={enableCellDetail}>
      <DataGridRowHeightContext.Provider value={rowHeightPreset}>
        {grid}
      </DataGridRowHeightContext.Provider>
    </DataGridCellDetailEnabledContext.Provider>
  );
}
